import React, { useRef, useState } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import { Popover } from "@mui/material";
import "./editorClasses.css";
import { EventDataInfo } from "../../MainPage/Tools/VirtualList/VirtualList";
import styles from "./editorStyles.module.css";

interface CheckBoxListProps {
  anchorEl: Element | null;
  open: boolean;
  checked: number[];
  items: EventDataInfo[];
  id?: string;
  handleToggle: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, value: EventDataInfo) => void;
  handleClose: (hasChanges?: boolean) => Promise<void> | void;
  onGetListCheckState?: (value: EventDataInfo) => boolean;
  onSetListCheckState?: (value: EventDataInfo, checked: boolean) => Promise<void> | void;
}

const CheckboxList = (props: CheckBoxListProps) => {
  const { handleClose, open, anchorEl, id, items, onGetListCheckState, onSetListCheckState } = props;
  const currentTarget = useRef<any>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const arrowDown = () => {
    if (!listRef.current) return;
    let el: any = listRef.current.querySelector(".focusCheckListItem");
    const firstChild = listRef.current.firstChild;
    const lastChild = listRef.current.lastChild;

    if (!el) {
      el = firstChild;
      el.focus();
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
      return;
    }

    if (el === lastChild) {
      el.classList.remove("focusCheckListItem");
      el = firstChild;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
    } else {
      el.classList.remove("focusCheckListItem");
      el = el.nextElementSibling;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
    }

    const parentEl = listRef.current.parentElement?.parentElement;
    if (!parentEl) return;
    const scrollingPos = el.getBoundingClientRect().y - parentEl.getBoundingClientRect().y;

    parentEl.scrollBy({
      left: 0,
      top: scrollingPos,
      behavior: "smooth",
    });
  };

  const arrowUp = () => {
    if (!listRef.current) return;
    let el: any = listRef.current.querySelector(".focusCheckListItem");
    const firstChild = listRef.current.firstChild;
    const lastChild = listRef.current.lastChild;

    if (!el) {
      el = lastChild;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
      return;
    }

    if (el === firstChild) {
      el.classList.remove("focusCheckListItem");
      el = lastChild;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
    } else {
      el.classList.remove("focusCheckListItem");
      el = el.previousElementSibling;
      el.classList.add("focusCheckListItem");
      currentTarget.current = el;
    }

    const parentEl = listRef.current.parentElement?.parentElement;
    if (!parentEl) return;
    const scrollingPos = el.getBoundingClientRect().y - parentEl.getBoundingClientRect().y;

    parentEl.scrollBy({
      left: 0,
      top: scrollingPos,
      behavior: "smooth",
    });
  };

  const onKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    switch (ev.code) {
      case "ArrowDown":
        arrowDown();
        break;
      case "ArrowUp":
        arrowUp();
        break;
      case "Space":
        ev.preventDefault();
        if (currentTarget.current) {
          const bttn = currentTarget.current.querySelector(`[listbutton="true"]`);
          if (bttn) bttn.click();
        }
        break;
      case "Enter":
        ev.stopPropagation();
        handleClose(hasChanges);
        break;
      case "Tab":
        ev.preventDefault();
        arrowDown();
        break;
    }
  };

  return (
    <Popover
      transitionDuration={50}
      onKeyDown={onKeyDown}
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        handleClose(hasChanges);
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <div
        ref={(el) => {
          if (!el) return;
          listRef.current = el.querySelector("ul");
        }}
        className={styles.container}
        data-checklist="true"
        style={{
          "--list-max-width": `${document.body.clientWidth / 2}px`,
        }}
      >
        <List className={styles.list}>
          {items?.map((value) => {
            const labelId = `checkbox-list-label-${value.id}`;
            const checked = onGetListCheckState
              ? onGetListCheckState(value)
              : props.checked.indexOf(Number(value.id)) !== -1;
            return (
              <ListItem title={value.label} key={value.id} className={styles.listItem} disablePadding>
                <ListItemButton
                  style={{ height: "inherit" }}
                  listbutton="true"
                  onClick={async (ev) => {
                    if (onSetListCheckState) {
                      await onSetListCheckState(value, !checked);
                    } else setHasChanges(true);
                    props.handleToggle(ev, value);
                  }}
                  dense
                >
                  <ListItemIcon className={styles.listItemIcon}>
                    <Checkbox
                      size="small"
                      edge="start"
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                      slotProps={{
                        input: {
                          "aria-labelledby": labelId,
                        },
                      }}
                    />
                  </ListItemIcon>

                  <ListItemText id={labelId} primary={value.label} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </div>
    </Popover>
  );
};
export default CheckboxList;
